import React, { useContext, useEffect, useState, useRef } from "react";
import AuthContext from "../../context/AuthContext.js";
import api from "../../services/AxiosConnection.js";
import { useNotification } from "../../context/NotificationContext.js";
import NotificationSnackbar from "../../utils/NotificationSnackbar.js";
import TrainDetails from "./TrainDetails.js";
import AlarmsAndWarnings from "./AlarmsAndWarnings.js";
import TrainLogList from "./TrainLogList.js";
import { Vortex } from "react-loader-spinner";
import AlarmsDialog from "./AlarmsDialog.js";
import { useLocation } from "react-router-dom";
import WarningsDialog from "./WarningsDialog.js";
import TrainCoachSlider from "./TrainCoachSlider.js";
import TempLogTable from "./TempLogTable.js";
import socket from "../../services/socket.js";

function MainView() {
  const location = useLocation();
  const { siteDb: routeSiteDb, trainId: routeTrainId } = location.state || {};
  const {
    selectedSite,
    setSelectedSite,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useContext(AuthContext);
  const [trainLogList, setTrainLogList] = useState([]);
  const [coachData, setCoachData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trainDetails, setTrainDetails] = useState(null);
  const [temperatureLogs, setTemperatureLogs] = useState([]);
  const [groupedTemperatureData, setGroupedTemperatureData] = useState(null);
  const [alarms, setAlarms] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const { notification, closeNotification } = useNotification();
  const { triggerNotification } = useNotification();
  const filteredAlarms = alarms.filter((alarm) => !alarm.Acknowledged);
  const filteredWarnings = warnings.filter((warning) => !warning.Acknowledged);
  const [highlightedTrainId, setHighlightedTrainId] = useState(null);
  const highlightedTrainIdRef = useRef(highlightedTrainId);
  useEffect(() => {
    highlightedTrainIdRef.current = highlightedTrainId;
  }, [highlightedTrainId]);

  // Ref to track the height of the right column
  const rightColumnRef = useRef(null);
  const [rightColumnHeight, setRightColumnHeight] = useState(0);

  // Effect to update the height of the right column dynamically
  useEffect(() => {
    const updateHeight = () => {
      if (rightColumnRef.current) {
        const height = rightColumnRef.current.offsetHeight;
        setRightColumnHeight(height);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [trainDetails, coachData, temperatureLogs, alarms, warnings]);

  // Sync site from route state if provided
  useEffect(() => {
    if (routeSiteDb && routeSiteDb !== selectedSite) {
      setSelectedSite(routeSiteDb);
    }
  }, [routeSiteDb, selectedSite, setSelectedSite]);

  // Fetch grouped temperature data for Detail View charts
  useEffect(() => {
    const fetchGroupedTemperatureData = async () => {
      if (!selectedSite || !highlightedTrainId) return;

      try {
        const response = await api.get("/main-view/getGroupedTemperatureData", {
          params: { siteDb: selectedSite, trainId: highlightedTrainId },
        });
        setGroupedTemperatureData(response.data);
      } catch (error) {
        console.error("Error fetching temperature data:", error);
      }
    };

    fetchGroupedTemperatureData();
  }, [highlightedTrainId, selectedSite]);

  // Fetch coach data for Detail View train composition diagram
  useEffect(() => {
    const fetchCoachData = async () => {
      setCoachData(null);
      if (!selectedSite || !highlightedTrainId) return;

      try {
        const response = await api.get("/main-view/getCoachData", {
          params: { siteDb: selectedSite, trainId: highlightedTrainId },
        });
        setCoachData(response.data);
      } catch (error) {
        setCoachData(null);
        console.error("Error fetching coach data:", error);
      }
    };

    fetchCoachData();
  }, [highlightedTrainId, selectedSite]);

  // Reusable helper to fetch train details, temperature logs, and alerts
  const fetchTrainDetailsData = async (trainId, site) => {
    const currentSite = site || selectedSite;
    if (!trainId || !currentSite) return;
    setHighlightedTrainId(trainId);

    try {
      const response = await api.get("/simple-view/getTrainDetailsByTrainId", {
        params: { siteDb: currentSite, trainId },
      });
      if (response.data?.trainDetails?.length > 0) {
        setTrainDetails(response.data.trainDetails[0]);
      } else {
        setTrainDetails(null);
      }
    } catch (error) {
      console.error("Error fetching train details", error);
      setTrainDetails(null);
    }

    try {
      const temperatureLogResponse = await api.get(
        "/simple-view/getTemperatureLogByTrainId",
        {
          params: { siteDb: currentSite, trainId },
        }
      );
      if (temperatureLogResponse.data?.temperatureLogs?.length > 0) {
        setTemperatureLogs(temperatureLogResponse.data.temperatureLogs);
      } else {
        setTemperatureLogs([]);
      }
    } catch (error) {
      console.error("Error fetching temperature logs", error);
      setTemperatureLogs([]);
    }

    try {
      const alarmAndWarningResponse = await api.get(
        "/simple-view/getAlertsByTrainId",
        {
          params: { siteDb: currentSite, trainId },
        }
      );
      setAlarms(alarmAndWarningResponse.data?.alarms || []);
      setWarnings(alarmAndWarningResponse.data?.warnings || []);
    } catch (error) {
      console.error("Error fetching alerts", error);
      setAlarms([]);
      setWarnings([]);
    }
  };

  // Primary unified effect to load train logs and initial train details in a single loading cycle
  useEffect(() => {
    if (!selectedSite) return;
    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        let response;
        if (fromDate && toDate) {
          response = await api.get("/simple-view/getTrainIdsByDateRange", {
            params: { siteDb: selectedSite, fromDate, toDate },
          });
        } else {
          response = await api.get("/simple-view/getAllTrainIds", {
            params: { siteDb: selectedSite },
          });
        }

        const trains = response.data?.trainIds || [];
        if (isMounted) {
          setTrainLogList(trains);
        }

        if (trains.length > 0) {
          const targetTrainId = routeTrainId && trains.some((t) => t.TrainId === routeTrainId)
            ? routeTrainId
            : trains[0]?.TrainId;

          if (isMounted && targetTrainId) {
            await fetchTrainDetailsData(targetTrainId, selectedSite);
          }
        } else if (isMounted) {
          setTrainDetails(null);
          setTemperatureLogs([]);
          setAlarms([]);
          setWarnings([]);
          setHighlightedTrainId(null);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, routeSiteDb, routeTrainId]);

  // Socket.IO real-time listener & 5-minute interval polling without disruptive page reload or full-screen loading
  useEffect(() => {
    if (!selectedSite) return;

    if (socket.connected) {
      socket.emit("join_site", selectedSite);
    }

    const handleConnect = () => {
      socket.emit("join_site", selectedSite);
    };

    const fetchLatestTrainData = async (isSocketEvent = false) => {
      try {
        let response;
        if (fromDate && toDate) {
          response = await api.get("/simple-view/getTrainIdsByDateRange", {
            params: { siteDb: selectedSite, fromDate, toDate },
          });
        } else {
          response = await api.get("/simple-view/getAllTrainIds", {
            params: { siteDb: selectedSite },
          });
        }
        const newTrainIds = response.data?.trainIds;
        if (Array.isArray(newTrainIds) && newTrainIds.length > 0) {
          setTrainLogList((prev) => {
            const hasNewTrain = prev.length > 0 && prev[0]?.TrainId !== newTrainIds[0]?.TrainId;
            const countChanged = prev.length !== newTrainIds.length;

            if (hasNewTrain) {
              const latestTrainId = newTrainIds[0].TrainId;
              // If user is watching live (no custom date range selected)
              if (!fromDate && !toDate) {
                // If user was on the top train or no train selected yet, automatically load new train details
                if (!highlightedTrainIdRef.current || highlightedTrainIdRef.current === prev[0]?.TrainId) {
                  fetchTrainDetailsData(latestTrainId, selectedSite);
                }
                triggerNotification(`New train detected: ${latestTrainId}`, "info");
              } else {
                triggerNotification("New train log received from site", "info");
              }
              return newTrainIds;
            } else if (countChanged || prev.length === 0) {
              return newTrainIds;
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("Error refreshing train logs:", err);
      }
    };

    const handleDataUpdated = (updatedData) => {
      if (updatedData && updatedData.siteDb && updatedData.siteDb !== selectedSite) return;
      fetchLatestTrainData(true);
    };

    socket.on("connect", handleConnect);
    socket.on("division_data_updated", handleDataUpdated);

    // 5 minutes fallback polling interval
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const intervalId = setInterval(() => {
      fetchLatestTrainData(false);
    }, FIVE_MINUTES_MS);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("division_data_updated", handleDataUpdated);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite, fromDate, toDate]);

  const handleTrainLogClick = async (trainId) => {
    if (!trainId || !selectedSite) return;
    setLoading(true);
    try {
      await fetchTrainDetailsData(trainId, selectedSite);
      triggerNotification("Train data fetched successfully!", "success");
    } catch (error) {
      console.error("Error fetching train details on click:", error);
      triggerNotification("No data found for the selected train!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckLiveTrainStatus = async () => {
    if (!selectedSite) {
      triggerNotification("No site selected", "error");
      return;
    }

    setLoading(true);
    setFromDate(""); // Reset fromDate in context
    setToDate(""); // Reset toDate in context

    try {
      const response = await api.get("/simple-view/getAllTrainIds", {
        params: { siteDb: selectedSite },
      });

      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);

      if (trains.length > 0) {
        triggerNotification("Live train data fetched successfully", "success");
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      } else {
        triggerNotification("No live train data available for this site", "warning");
        setTrainDetails(null);
        setTemperatureLogs([]);
        setAlarms([]);
        setWarnings([]);
        setHighlightedTrainId(null);
      }
    } catch (error) {
      console.error("Error fetching live train logs", error);
      triggerNotification("Error fetching live train data", "error");
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    const validateDates = () => {
      if (!fromDate || !toDate) {
        triggerNotification("Please select both from and to dates.", "error");
        return false;
      }
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);
      fromDateObj.setHours(0, 0, 0, 0);
      toDateObj.setHours(0, 0, 0, 0);

      const twoMonthsInMs = 60 * 24 * 60 * 60 * 1000;
      if (toDateObj - fromDateObj > twoMonthsInMs) {
        triggerNotification("Date range cannot exceed 2 months.", "error");
        return false;
      }
      if (fromDateObj > toDateObj) {
        triggerNotification("From date should be earlier than to date.", "error");
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (toDateObj > today) {
        triggerNotification("To date should not be greater than today's date.", "error");
        return false;
      }
      return true;
    };

    if (!validateDates()) return;

    setLoading(true);

    try {
      const response = await api.get("/simple-view/getTrainIdsByDateRange", {
        params: { siteDb: selectedSite, fromDate, toDate },
      });

      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);

      if (trains.length > 0) {
        triggerNotification(
          `Trains From ${fromDate} to ${toDate} fetched Successfully`,
          "success"
        );
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      } else {
        triggerNotification(
          response.data?.message || `No train logs found from ${fromDate} to ${toDate}`,
          "warning"
        );
        setTrainDetails(null);
        setTemperatureLogs([]);
        setAlarms([]);
        setWarnings([]);
        setHighlightedTrainId(null);
      }
    } catch (error) {
      console.error("Error fetching train logs by date", error);
      triggerNotification("Error fetching train logs by date", "error");
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainLogs = async () => {
    setLoading(true);

    try {
      let response;
      if (fromDate && toDate) {
        response = await api.get("/simple-view/getTrainIdsByDateRange", {
          params: { siteDb: selectedSite, fromDate, toDate },
        });
      } else {
        response = await api.get("/simple-view/getAllTrainIds", {
          params: { siteDb: selectedSite },
        });
      }

      const trains = response.data?.trainIds || [];
      setTrainLogList(trains);
      if (trains.length > 0 && (!highlightedTrainId || !trains.some(t => t.TrainId === highlightedTrainId))) {
        await fetchTrainDetailsData(trains[0].TrainId, selectedSite);
      }
    } catch (error) {
      console.error("Error fetching train logs", error);
      setTrainLogList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemperatureLogs = async (trainId) => {
    if (!selectedSite || !trainId) return;

    try {
      const response = await api.get(
        "/simple-view/getTemperatureLogByTrainId",
        {
          params: { siteDb: selectedSite, trainId },
        }
      );
      if (
        response.data.temperatureLogs &&
        response.data.temperatureLogs.length > 0
      ) {
        setTemperatureLogs(response.data.temperatureLogs);
      } else {
        setTemperatureLogs([]);
      }
    } catch (error) {
      console.error("Error fetching temperature logs", error);
      setTemperatureLogs([]);
      triggerNotification(
        `Error fetching temperature logs for selected train`,
        "error"
      );
    }
  };

  const handleUpdateTemperatureLogs = async () => {
    if (!highlightedTrainId) return;

    setLoading(true);
    await fetchTemperatureLogs(highlightedTrainId);
    setLoading(false);
    triggerNotification("Temperature logs updated successfully!", "success");
  };

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={[
              "#2563eb",
              "#1e40af",
              "#60a5fa",
              "#3b82f6",
              "#1d4ed8",
              "#93c5fd",
            ]}
          />
        </div>
      )}

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      <div className="px-3 pt-2 pb-3 sm:px-4 sm:pt-2 sm:pb-4 flex-grow bg-gray-100 min-h-screen">
        <div className="flex flex-col lg:flex-row gap-4 h-full">
          {/* Left Column */}
          <div className="lg:w-1/4 flex flex-col h-full">
            {/* Train Log Selection */}
            <div className="bg-[#e6e7eb] p-4 rounded-xl mb-4 h-full flex flex-col shadow-sm">
              <h2 className="font-bold mb-4 text-black">Select Train Log</h2>
              <div className="space-y-4 flex-grow">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="block text-xs mb-1 text-gray-700">From Date:</label>
                    <input
                      type="date"
                      className="w-full p-1 text-sm rounded border border-gray-300 bg-white"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs mb-1 text-gray-700">To Date:</label>
                    <input
                      type="date"
                      className="w-full p-1 text-sm rounded border border-gray-300 bg-white"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    className="w-full sm:w-1/2 bg-blue-900 text-white py-1 text-sm rounded"
                    onClick={handleFilter}
                  >
                    Filter
                  </button>
                  <button
                    className="w-full sm:w-1/2 bg-blue-900 text-white py-1 text-sm rounded"
                    onClick={handleCheckLiveTrainStatus}
                  >
                    Live Train
                  </button>
                </div>
              </div>
            </div>

            {/* Train Log List - Takes remaining height */}
            <div className="flex-grow">
              <TrainLogList
                trainLogList={trainLogList}
                onLogClick={handleTrainLogClick}
                highlightedTrainId={highlightedTrainId}
                fetchTrainLogs={fetchTrainLogs}
                rightColumnHeight={rightColumnHeight}
              />
            </div>
          </div>

          {/* Right Column */}
          <div
            ref={rightColumnRef}
            className="lg:w-3/4 w-full mx-auto flex flex-col"
          >
            {trainDetails && <TrainDetails trainDetails={trainDetails} />}
            <TrainCoachSlider
              coachData={coachData}
              groupedTemperatureData={groupedTemperatureData}
              trainId={highlightedTrainId}
              temperatureLogs={temperatureLogs}
              trainDetails={trainDetails}
            />
            <TempLogTable
              temperatureLogs={temperatureLogs}
              trainId={highlightedTrainId}
              onUpdate={handleUpdateTemperatureLogs}
            />
            <AlarmsAndWarnings alarms={alarms} warnings={warnings} trainId={highlightedTrainId} />
          </div>
        </div>
      </div>

      <AlarmsDialog alarms={filteredAlarms} />
      <WarningsDialog warnings={filteredWarnings} />
    </div>
  );
}

export default MainView;